/**
 * ReunificationJoinPromptContext — cross-site "join this reunification" prompt.
 *
 * When the SocketMessageRouter's reunification handler sees a
 * `reunification.event.activated` frame, it calls `enqueuePrompt()` here.
 * The provider gates on `canManageReunificationFor` (vendor OR
 * `reunification.activate_event` + school membership) and queues the
 * payload. A sibling `ReunificationJoinPromptUI` component (mounted
 * inside BrowserRouter) renders the blocking Join/Dismiss dialog and
 * owns the `useNavigate()` dependency — this split is what lets the
 * provider sit ABOVE the router so `SocketMessageRouter` can pull
 * `enqueueJoinPrompt` from context and thread it into HandlerDeps.
 *
 * On Join the provider (1) switches activeOrganization if the event
 * belongs to a different site, (2) sets `pendingDeepLink` which
 * `EventsOverview` reads to auto-open the EventDetailDrawer. The UI
 * component then calls `navigate('/events')`.
 *
 * Dedupe: `event_id`s seen in this session (whether joined or dismissed)
 * never re-queue, so a replay/snapshot-on-reconnect won't re-prompt.
 *
 * Scope limitation: the consumer joins no `school-reunification-{school_id}`
 * group on connect (and has no client `subscribe` action), so SMS receives
 * this broadcast only via the user's own `user-{id}` group — the prompt fires
 * only for activations where the authenticated user is a notified party on
 * the event. Cross-site coverage for arbitrary managers requires the
 * server-side group join.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { useUser } from '@aegix/auth-identity';
import {
  useActiveOrganization,
  useIsVendorMasqueradeAvailable,
} from '@aegix/auth-organization';
import { usePermissions } from '@aegix/auth-permissions';
import { useSetActiveOrganization } from '../hooks/useSetActiveOrganization';
import type { ActiveOrganization, SMSUser } from '../lib/sms-auth';
import { useUserSchoolMembership } from '../hooks/queries/useSchool';
import {
  canManageReunificationFor,
  MANAGE_REUNIFICATION_PERMISSION,
} from '../utils/canManageReunificationFor';

export interface ReunificationJoinPromptPayload {
  event_id: string;
  school_id: number;
  school_name?: string;
  status?: string;
  is_drill?: boolean;
  incident_type?: string;
  started_at?: string;
  activated_by?: string;
}

/** Deep link consumed by `EventsOverview` to auto-open the drawer on land. */
export interface ReunificationDeepLink {
  event_id: string;
  event_type: 'reunification' | 'drill';
}

interface ReunificationJoinPromptContextValue {
  /** Dispatch point for the socket handler. Silently filters non-matches. */
  enqueuePrompt: (payload: ReunificationJoinPromptPayload) => void;
  /** Head of the prompt queue, or null when nothing is pending. */
  headPrompt: ReunificationJoinPromptPayload | null;
  /**
   * User dismissed the prompt — pop the head; dedupe keeps the
   * event_id from re-queueing this session.
   */
  dismissHead: () => void;
  /**
   * User joined — switch activeOrganization if different, stash a deep
   * link for `EventsOverview`, and pop the head. Returns the destination
   * path the UI component should `navigate()` to.
   */
  joinHead: () => string | null;
  /** Consumed by `EventsOverview`; returns and clears in one call. */
  consumePendingDeepLink: () => ReunificationDeepLink | null;
}

const Context = createContext<ReunificationJoinPromptContextValue | undefined>(
  undefined,
);

export function ReunificationJoinPromptProvider({ children }: PropsWithChildren) {
  const user = useUser<SMSUser>();
  const permissions = usePermissions();
  const isVendorUser = useIsVendorMasqueradeAvailable();
  const activeOrganization = useActiveOrganization() as ActiveOrganization | null;
  const setActiveOrganization = useSetActiveOrganization();

  const { data: membershipData } = useUserSchoolMembership({
    baseuser: user?.id ?? 0,
  });
  const memberships = membershipData?.results;

  const [queue, setQueue] = useState<ReunificationJoinPromptPayload[]>([]);
  const seenEventIdsRef = useRef<Set<string>>(new Set());
  const pendingDeepLinkRef = useRef<ReunificationDeepLink | null>(null);

  const enqueuePrompt = useCallback(
    (payload: ReunificationJoinPromptPayload) => {
      if (!payload.event_id) return;
      if (seenEventIdsRef.current.has(payload.event_id)) return;

      const allowed = canManageReunificationFor({
        permissions,
        memberships,
        isVendorUser,
        schoolId: payload.school_id,
      });
      if (!allowed) return;

      seenEventIdsRef.current.add(payload.event_id);
      setQueue((prev) => [...prev, payload]);
    },
    [permissions, memberships, isVendorUser],
  );

  const headPrompt = queue[0] ?? null;

  const dismissHead = useCallback(() => {
    setQueue((prev) => prev.slice(1));
  }, []);

  const joinHead = useCallback((): string | null => {
    const head = queue[0];
    if (!head) return null;

    if (
      head.school_id !== activeOrganization?.id &&
      Number.isFinite(head.school_id)
    ) {
      // Fallback shape — the `activeOrganization` consumer refetches
      // richer data once it observes the id change. We pass what we know.
      setActiveOrganization({
        id: head.school_id,
        name: head.school_name ?? '',
        public_id: '',
        status: 'active',
      });
    }

    pendingDeepLinkRef.current = {
      event_id: head.event_id,
      event_type: head.is_drill ? 'drill' : 'reunification',
    };

    setQueue((prev) => prev.slice(1));
    return '/events';
  }, [queue, activeOrganization?.id, setActiveOrganization]);

  const consumePendingDeepLink = useCallback(() => {
    const link = pendingDeepLinkRef.current;
    pendingDeepLinkRef.current = null;
    return link;
  }, []);

  const value = useMemo<ReunificationJoinPromptContextValue>(
    () => ({
      enqueuePrompt,
      headPrompt,
      dismissHead,
      joinHead,
      consumePendingDeepLink,
    }),
    [enqueuePrompt, headPrompt, dismissHead, joinHead, consumePendingDeepLink],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

/** Hook for dispatch, UI, and deep-link consumers. */
export function useReunificationJoinPrompt(): ReunificationJoinPromptContextValue {
  const ctx = useContext(Context);
  if (!ctx) {
    throw new Error(
      'useReunificationJoinPrompt must be used within a ReunificationJoinPromptProvider',
    );
  }
  return ctx;
}

/**
 * Non-throwing accessor — used by `SocketMessageRouter` so test harnesses
 * that mount the router without the prompt provider still function.
 * The router degrades gracefully: omitting `enqueueJoinPrompt` from
 * HandlerDeps preserves the existing cache-invalidation-only behavior.
 */
export function useOptionalReunificationJoinPrompt():
  | ReunificationJoinPromptContextValue
  | null {
  return useContext(Context) ?? null;
}

export { MANAGE_REUNIFICATION_PERMISSION };
